import React, { useState } from "react";
import { MessageCircle, Link2 } from "lucide-react";
import { toast } from "sonner";
import { API } from "../lib/api";
import { useLang } from "../lib/i18n";
import { printDoc } from "../lib/printDoc";
import { LetterDoc } from "./docs";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";

export function LetterDialog({ doc, title, onClose, testid = "letter", onShared }) {
  const { t } = useLang();
  const [mode, setMode] = useState("a4");
  const [draft, setDraft] = useState(false);
  const phoneDigits = (p) => { const d = (p || "").replace(/[^0-9]/g, ""); return d.length === 8 ? `670${d}` : d; };
  const shareWa = async () => {
    const base = process.env.REACT_APP_BACKEND_URL;
    const url = `${base}${doc.share_path}`;
    const digits = phoneDigits(doc.customer?.phone);
    const msg = `${doc.shop?.name} — ${doc.notice_no}\n${doc.sections?.[0]?.title || ""}\n${t("ntc.deadline")}: ${doc.deadline}\n${url}`;
    window.open(`https://wa.me/${digits}?text=${encodeURIComponent(msg)}`, "_blank");
    try { await API.post(`/shop/notices/${doc.id}/shared`); onShared && onShared(); } catch { /* best effort */ }
  };
  const copyLink = async () => {
    try { await navigator.clipboard.writeText(`${process.env.REACT_APP_BACKEND_URL}${doc.share_path}`); toast.success(t("ntc.link_copied")); } catch { toast.error(t("ntc.link_copy_failed")); }
  };
  return (
    <Dialog open={!!doc} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-white max-w-3xl max-h-[90vh] overflow-y-auto" data-testid={`${testid}-dialog`}>
        {doc && (
          <>
            <DialogHeader><DialogTitle className="font-heading">{title}</DialogTitle></DialogHeader>
            <LetterDoc doc={doc} printMode={mode} draftMark={draft} testid={`${testid}-body`} />
            <div className="flex flex-wrap items-center gap-2 mt-4">
              <div className="flex rounded-lg border border-slate-300 overflow-hidden text-xs">
                <button onClick={() => setMode("a4")} className={`px-3 py-1.5 ${mode === "a4" ? "bg-slate-900 text-white" : "bg-white text-slate-600"}`}>{t("print.mode_a4")}</button>
                <button onClick={() => setMode("thermal")} className={`px-3 py-1.5 ${mode === "thermal" ? "bg-slate-900 text-white" : "bg-white text-slate-600"}`}>{t("print.mode_thermal")}</button>
              </div>
              <button onClick={() => setDraft(!draft)} className={`px-3 py-1.5 rounded-lg border text-xs ${draft ? "border-red-400 bg-red-50 text-red-700" : "border-slate-300 text-slate-600"}`}>{t("print.draft")}</button>
              {doc.share_path && (
                <>
                  <button data-testid={`${testid}-whatsapp-button`} onClick={shareWa} disabled={!phoneDigits(doc.customer?.phone)}
                    title={!phoneDigits(doc.customer?.phone) ? t("ntc.no_phone") : ""}
                    className="px-3 py-1.5 rounded-lg border border-green-300 text-green-700 hover:bg-green-50 text-xs flex items-center gap-1.5 disabled:opacity-40">
                    <MessageCircle className="h-3.5 w-3.5" />{t("ntc.send_whatsapp")}
                  </button>
                  <button data-testid={`${testid}-copy-link-button`} onClick={copyLink}
                    className="px-3 py-1.5 rounded-lg border border-slate-300 text-slate-600 hover:bg-slate-50 text-xs flex items-center gap-1.5">
                    <Link2 className="h-3.5 w-3.5" />{t("ntc.copy_link")}
                  </button>
                </>
              )}
              <Button data-testid={`${testid}-print-button`} className="flex-1 bg-slate-900 hover:bg-slate-800 text-white"
                onClick={() => printDoc(document.querySelector(`[data-testid="${testid}-body"]`), { mode, draft, title: doc.notice_no || doc.addendum_no })}>
                {t("repay.print")}
              </Button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
